import { useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import DataService from "../services/data.service";
import RegisterService from "../services/register.service";

const CreateUser = () => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("User");
  const [username, setUsername] = useState("");
  const [clientName, setClientName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [clientExist, setClientExist] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  const generateRandomId = () => {
    const generatedValue = Math.floor(Math.random() * 900000) + 100000;
    setAccountNumber(generatedValue);
  };

  const validate = () => {
    if (!username || !email || !phoneNumber || !password || !confirmPassword) {
      setErrorMessage("Complete all required user fields before saving.");
      setSuccessMessage("");
      return false;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      setSuccessMessage("");
      return false;
    }

    if (role === "User" && !clientExist && (!clientName || !accountNumber)) {
      setErrorMessage("Provide client name and account number for the new client profile.");
      setSuccessMessage("");
      return false;
    }

    return true;
  };

  const saveClientUser = () => {
    RegisterService.registerClientUser(username, email, password, phoneNumber).then(
      (response) => {
        const userId = response.data.id;

        if (role === "User" && !clientExist) {
          DataService.createClient(accountNumber.toString(), clientName, phoneNumber, userId).then(() => {
            setSuccessMessage("User and linked client created successfully.");
            setErrorMessage("");
            navigate("/users");
            window.location.reload();
          });
        } else {
          setSuccessMessage("User created successfully.");
          setErrorMessage("");
          navigate("/users");
          window.location.reload();
        }
      },
      (error) => {
        const responseMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setErrorMessage(responseMessage);
        setSuccessMessage("");
      }
    );
  };

  const saveAdminUser = () => {
    RegisterService.register(username, email, password, phoneNumber).then(
      () => {
        setSuccessMessage("Administrator created successfully.");
        setErrorMessage("");
        navigate("/users");
        window.location.reload();
      },
      (error) => {
        const responseMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setErrorMessage(responseMessage);
        setSuccessMessage("");
      }
    );
  };

  const handleSaveUser = (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    if (role === "User") {
      saveClientUser();
    } else {
      saveAdminUser();
    }
  };

  return (
    <div className="portal-page-stack">
      <section className="content-card content-card--padded page-form-card">
        <div className="section-heading">
          <div>
            <p className="section-heading__eyebrow">Access provisioning</p>
            <h4>Create a portal user</h4>
          </div>
          <Link to="/users" className="section-link">
            Back to users
          </Link>
        </div>
        <p className="content-card__supporting">
          Provision a new operator or administrator and optionally create a linked client profile for user accounts.
        </p>
        <Form onSubmit={handleSaveUser}>
          <div className="portal-form-grid portal-form-grid--stacked">
            <Form.Group controlId="createUserUsername">
              <Form.Label>Username</Form.Label>
              <Form.Control type="text" value={username} onChange={(event) => setUsername(event.target.value)} />
            </Form.Group>
            <Form.Group controlId="createUserEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control type="text" value={email} onChange={(event) => setEmail(event.target.value)} />
            </Form.Group>
            <Form.Group controlId="createUserPhoneNumber">
              <Form.Label>Phone number</Form.Label>
              <Form.Control type="text" value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} />
            </Form.Group>
            <Form.Group controlId="createUserPassword">
              <Form.Label>Password</Form.Label>
              <Form.Control type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
            </Form.Group>
            <Form.Group controlId="createUserConfirmPassword">
              <Form.Label>Confirm password</Form.Label>
              <Form.Control type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
            </Form.Group>
            <Form.Group controlId="createUserRole">
              <Form.Label>Role</Form.Label>
              <Form.Control as="select" value={role} onChange={(event) => setRole(event.target.value)}>
                <option value="User">User</option>
                <option value="Admin">Admin</option>
              </Form.Control>
            </Form.Group>
          </div>

          <div className="form-toggle-row mt-3 mb-4">
            <Form.Check
              type="checkbox"
              label="Link to an existing client profile"
              checked={clientExist}
              onChange={() => setClientExist((currentValue) => !currentValue)}
              disabled={role !== "User"}
            />
          </div>

          {role === "User" && !clientExist ? (
            <div className="subsection-panel">
              <div className="section-heading section-heading--compact">
                <div>
                  <p className="section-heading__eyebrow">Client setup</p>
                  <h4>Create linked client profile</h4>
                </div>
              </div>
              <div className="portal-form-grid portal-form-grid--stacked">
                <Form.Group controlId="createUserClientName">
                  <Form.Label>Client name</Form.Label>
                  <Form.Control type="text" value={clientName} onChange={(event) => setClientName(event.target.value)} />
                </Form.Group>
                <Form.Group controlId="createUserAccountNumber">
                  <Form.Label>Account number</Form.Label>
                  <div className="inline-control-row">
                    <Form.Control type="text" disabled value={accountNumber} />
                    <Button variant="outline-primary" onClick={generateRandomId}>
                      Generate
                    </Button>
                  </div>
                </Form.Group>
              </div>
            </div>
          ) : null}

          <div className="button-row mt-4">
            <Button id="saveUser" variant="primary" type="submit">
              Save user
            </Button>
          </div>
        </Form>
        {errorMessage ? <Alert variant="danger" className="mt-3 mb-0">{errorMessage}</Alert> : null}
        {successMessage ? <Alert variant="success" className="mt-3 mb-0">{successMessage}</Alert> : null}
      </section>
    </div>
  );
};

export default CreateUser;
